// Pure Similarity scoring layer (04j-a), ported VERBATIM from
// js/renderers-similarity.js — "who does this player's real statistical profile most resemble,"
// across the whole ~1,087-player database. No ML, no correlation: every number is a plain
// arithmetic comparison of two real per-90 values against the metric's own whole-population
// range. The constants below are the source of truth (SIMILARITY_ALGORITHM.md §7 documents them).
//
// Kept pure (population + axes passed in) so the correctness-critical maths is unit-tested and
// unchanged at the Track-2 cutover. Builds on the already-ported radar-view (axisRawValue /
// isLowerBetter) + the StatsFilterKit engine (matchesFilters) — never window.RadarKit.
//
// PERF: the legacy cached wholePopulationValues per (season|key). We instead precompute each
// axis's range ONCE per results computation (metricRangesForAxes) and thread it through
// compareTwoPlayers — same effect, no module-level cache, still pure.

import type { RawPlayerRecord } from "../data/snapshot.js";
import { axisRawValue, isLowerBetter } from "./radar-view.js";
import { matchesFilters, type ActiveFilters, type KitPlayer, type RangeFilterConfig } from "../engines/stats-filter-kit.js";

// --- tunable constants (verbatim; SIMILARITY_ALGORITHM.md §7) ------------------

/** Whether one axis counts as "a match": |diff| ≤ range × this. */
export const MATCH_TOLERANCE_PCT = 0.045;
/** The credit a match right at the tolerance's edge earns (a dead-on match earns 1.0). */
export const MIN_MATCH_CREDIT = 0.9;
/** Minimum real shared axes before a score is computed at all (else honestly excluded). */
export const MIN_SIMILARITY_AXES = 3;
/** Net-advantage share bar for calling a pair a genuine upgrade/downgrade vs lateral. */
export const QUALITY_NET_THRESHOLD_FRAC = 0.25;
/** Minimum score (%) to appear in the ranked list at all. */
export const MIN_SIMILARITY_PCT = 50;

// --- types --------------------------------------------------------------------

export interface MetricRange {
  min: number;
  max: number;
}
export type QualityBucket = "upgrade" | "downgrade" | "lateral" | "unknown";

export interface SimilarityResult {
  candidate: RawPlayerRecord;
  /** creditSum / sharedCount × 100, rounded to 1dp (graduated credit, not a flat match count). */
  similarityPct: number;
  matchCount: number;
  aheadCount: number;
  behindCount: number;
  sharedAxisCount: number;
  totalAxisCount: number;
  netAdvantage: number;
}

export interface AxisComparison {
  diff: number;
  span: number;
  tolerance: number;
  isMatch: boolean;
  /** True when the candidate is ahead in the real, polarity-aware "good direction" sense. */
  candidateAhead: boolean;
}

// --- age helpers (shared with the report; per-file dupe, renderers-similarity.js:13) ----------

/** Age in whole years from a dob, or null. */
export function ageFromDob(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const monthDiff = now.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

// --- scoring ------------------------------------------------------------------

/** Graduated match credit: full 1.0 at diff=0, MIN_MATCH_CREDIT at the tolerance's edge, linear
 *  in between. renderers-similarity.js:139. */
export function axisMatchCredit(diff: number, tolerance: number): number {
  if (tolerance <= 0) return diff === 0 ? 1 : 0;
  const closeness = Math.max(0, 1 - diff / tolerance); // 1 at diff=0, 0 at diff=tolerance
  return MIN_MATCH_CREDIT + (1 - MIN_MATCH_CREDIT) * closeness;
}

/** Every real captured value for one metric across the population at a season, ascending.
 *  Population passed IN (pure). renderers-similarity.js:158. */
export function wholePopulationValues(players: Record<string, RawPlayerRecord>, seasonCode: string, key: string): number[] {
  const values: number[] = [];
  for (const p of Object.values(players)) {
    const v = axisRawValue(p, seasonCode, key);
    if (v != null && !Number.isNaN(v)) values.push(v);
  }
  values.sort((a, b) => a - b);
  return values;
}

/** The metric's real observed [min,max] across the population at a season — a stable fact about
 *  the metric, deliberately whole-population (not template-scoped) and anchored to the BASE
 *  player's season. Needs ≥2 real values, else null (honest exclusion). renderers-similarity.js:180. */
export function metricRange(players: Record<string, RawPlayerRecord>, seasonCode: string, key: string): MetricRange | null {
  const values = wholePopulationValues(players, seasonCode, key);
  if (values.length < 2) return null;
  return { min: values[0]!, max: values[values.length - 1]! };
}

/** Precompute each axis's whole-population range at the base season ONCE (the legacy's per-key
 *  cache, done purely) so a full candidate scan doesn't recompute it per pair. */
export function metricRangesForAxes(
  players: Record<string, RawPlayerRecord>,
  baseSeason: string,
  axisKeys: readonly string[],
): Map<string, MetricRange | null> {
  const map = new Map<string, MetricRange | null>();
  for (const key of axisKeys) map.set(key, metricRange(players, baseSeason, key));
  return map;
}

/** The one per-axis computation both the score and the report build on: is this axis a match
 *  (within toleranceFrac of the metric's real range), and if not, who's ahead (polarity-aware).
 *  null when either raw value or the range is missing. renderers-similarity.js:198. */
export function axisComparison(
  baseRaw: number | null | undefined,
  candRaw: number | null | undefined,
  range: MetricRange | null | undefined,
  toleranceFrac: number,
  lowerBetter: boolean,
): AxisComparison | null {
  if (baseRaw == null || Number.isNaN(baseRaw) || candRaw == null || Number.isNaN(candRaw) || !range) return null;
  const span = range.max - range.min;
  const tolerance = span * toleranceFrac;
  const diff = Math.abs(baseRaw - candRaw);
  return {
    diff,
    span,
    tolerance,
    isMatch: diff <= tolerance,
    candidateAhead: lowerBetter ? candRaw < baseRaw : candRaw > baseRaw,
  };
}

/** Compare a base/candidate pair over the given axes, using precomputed base-season ranges.
 *  null when fewer than MIN_SIMILARITY_AXES shared axes. renderers-similarity.js:212. */
export function compareTwoPlayers(
  base: RawPlayerRecord,
  baseSeason: string,
  candidate: RawPlayerRecord,
  candSeason: string,
  axisKeys: readonly string[],
  rangeByKey: Map<string, MetricRange | null>,
): SimilarityResult | null {
  let sharedCount = 0;
  let matchCount = 0;
  let aheadCount = 0;
  let behindCount = 0;
  let creditSum = 0;
  for (const key of axisKeys) {
    const baseRaw = axisRawValue(base, baseSeason, key);
    const candRaw = axisRawValue(candidate, candSeason, key);
    const cmp = axisComparison(baseRaw, candRaw, rangeByKey.get(key) ?? null, MATCH_TOLERANCE_PCT, isLowerBetter(key));
    if (!cmp) continue;
    sharedCount++;
    if (cmp.isMatch) {
      matchCount++;
      creditSum += axisMatchCredit(cmp.diff, cmp.tolerance);
    } else if (cmp.candidateAhead) {
      aheadCount++;
    } else {
      behindCount++;
    }
  }
  if (sharedCount < MIN_SIMILARITY_AXES) return null;
  return {
    candidate,
    // One decimal (renderers-similarity.js:237) — a whole-number % over ~11 axes moves in ~9pt
    // jumps; built from creditSum (graduated), not a flat matchCount, so a merely-close pair
    // stays below 100.0 while a near-identical one still reaches it.
    similarityPct: Math.round((creditSum / sharedCount) * 1000) / 10,
    matchCount,
    aheadCount,
    behindCount,
    sharedAxisCount: sharedCount,
    totalAxisCount: axisKeys.length,
    netAdvantage: aheadCount - behindCount,
  };
}

/** Upgrade / downgrade / lateral from the net advantage over the shared axes.
 *  renderers-similarity.js:250. */
export function qualityBucket(netAdvantage: number | null | undefined, sharedAxisCount: number): QualityBucket {
  if (netAdvantage == null || !sharedAxisCount) return "unknown";
  const netFrac = netAdvantage / sharedAxisCount;
  if (netFrac >= QUALITY_NET_THRESHOLD_FRAC) return "upgrade";
  if (netFrac <= -QUALITY_NET_THRESHOLD_FRAC) return "downgrade";
  return "lateral";
}

/** Absolute Age/Height/Weight ranges (a database-wide search has no reference player), identical
 *  numbers to Ranking's/Comparison's — the prototype's own per-file dupe. renderers-similarity.js:283. */
export const SIMILARITY_RANGE_FILTERS: RangeFilterConfig = {
  age: { label: "Age range", chipLabel: "Age", unit: "y", relative: false, domain: { min: 15, max: 42 }, getValue: (p) => ageFromDob(p.dob ?? null) },
  height: { label: "Height range", chipLabel: "Height", unit: "cm", relative: false, domain: { min: 151, max: 185 }, getValue: (p) => (typeof p.height === "number" ? p.height : null) },
  weight: { label: "Weight range", chipLabel: "Weight", unit: "kg", relative: false, domain: { min: 48, max: 81 }, getValue: (p) => (typeof p.weight === "number" ? p.weight : null) },
};

export interface SimilarityResultsParams {
  players: Record<string, RawPlayerRecord>;
  base: RawPlayerRecord;
  baseSeason: string;
  axisKeys: readonly string[];
  activeFilters: ActiveFilters;
  rangeFilters?: RangeFilterConfig;
}

/** The ranked candidate list: every other player passing the bio filters AND scoring above
 *  MIN_SIMILARITY_PCT, at their own current season, sorted by similarity desc.
 *  renderers-similarity.js:263. */
export function similarityResultsFor({
  players,
  base,
  baseSeason,
  axisKeys,
  activeFilters,
  rangeFilters = SIMILARITY_RANGE_FILTERS,
}: SimilarityResultsParams): SimilarityResult[] {
  const rangeByKey = metricRangesForAxes(players, baseSeason, axisKeys);
  const refPlayer = base as unknown as KitPlayer;
  const out: SimilarityResult[] = [];
  for (const name of Object.keys(players)) {
    if (name === base.name) continue;
    const pl = players[name]!;
    if (!matchesFilters(pl as unknown as KitPlayer, activeFilters, refPlayer, rangeFilters)) continue;
    const cmp = compareTwoPlayers(base, baseSeason, pl, pl.currentSeason ?? baseSeason, axisKeys, rangeByKey);
    if (cmp && cmp.similarityPct > MIN_SIMILARITY_PCT) out.push(cmp);
  }
  out.sort((a, b) => b.similarityPct - a.similarityPct);
  return out;
}
